import {useCallback, useMemo, useRef, useState} from "react";
import {coverViewPort, MapTransform, toPlayerViewPort, ViewPort} from "@/encounter/model/ViewPort.ts";
import Size from "@/util/Size.ts";
import type Position from "@/util/Position.ts";

export const defaultStageSize: Size = {width: 800, height: 600};

type GameMasterViewPortOptions = {
    initialMapTransform: MapTransform;
    playerViewPortLocked: boolean;
    getPlayerViewPort: () => ViewPort;
    scheduleViewPortUpdate: (viewPort: ViewPort) => void;
};

export function useGameMasterViewPort({
    initialMapTransform,
    playerViewPortLocked,
    getPlayerViewPort,
    scheduleViewPortUpdate,
}: GameMasterViewPortOptions) {
    const [mapTransform, setMapTransform] = useState(initialMapTransform);
    const latestMapTransform = useRef(initialMapTransform);
    const stageSize = useRef(defaultStageSize);
    const mapInteracted = useRef(false);

    const applyMapTransform = useCallback((transform: MapTransform) => {
        latestMapTransform.current = transform;
        setMapTransform(transform);
    }, []);

    const getCurrentViewPort = useCallback(() =>
        toPlayerViewPort(latestMapTransform.current, stageSize.current), []);

    const updateMapTransform = useCallback((transform: MapTransform) => {
        mapInteracted.current = true;
        applyMapTransform(transform);
        if (!playerViewPortLocked) {
            scheduleViewPortUpdate(toPlayerViewPort(transform, stageSize.current));
        }
    }, [applyMapTransform, playerViewPortLocked, scheduleViewPortUpdate]);

    const resizeStage = useCallback((nextStageSize: Size) => {
        stageSize.current = nextStageSize;
        if (!mapInteracted.current) {
            applyMapTransform(coverViewPort(getPlayerViewPort(), nextStageSize));
            return;
        }

        if (!playerViewPortLocked) scheduleViewPortUpdate(getCurrentViewPort());
    }, [
        applyMapTransform,
        getCurrentViewPort,
        getPlayerViewPort,
        playerViewPortLocked,
        scheduleViewPortUpdate,
    ]);

    const focusPlayerViewPort = useCallback(() => {
        mapInteracted.current = false;
        applyMapTransform(coverViewPort(getPlayerViewPort(), stageSize.current));
    }, [applyMapTransform, getPlayerViewPort]);

    const focusWorldPosition = useCallback((position: Position) => {
        const scale = latestMapTransform.current.scale;
        mapInteracted.current = true;
        applyMapTransform({
            x: stageSize.current.width / 2 - position.x * scale,
            y: stageSize.current.height / 2 - position.y * scale,
            scale,
        });
    }, [applyMapTransform]);

    return useMemo(() => ({
        mapTransform,
        getCurrentViewPort,
        updateMapTransform,
        resizeStage,
        focusPlayerViewPort,
        focusWorldPosition,
    }), [
        focusPlayerViewPort,
        focusWorldPosition,
        getCurrentViewPort,
        mapTransform,
        resizeStage,
        updateMapTransform,
    ]);
}
